import { Dialog, type DialogProps } from "@cmsgov/ds-medicare-gov";
import {
  type ForwardRefRenderFunction,
  Fragment,
  useEffect,
  useId,
  useRef,
  type ReactNode,
  forwardRef,
} from "react";
import { FocusTrap } from "focus-trap-react";
import classNames from "classnames";
import { TESTING } from "../config";
import { PortalWrapper } from "./PortalWrapper";
import type { FCWithChildren } from "../@types";
import { useFocusTrapOptions } from "../helpers/focusTrapHelpers";
import { useMgovRedesignClass } from "../helpers/flag-hooks/useMgovRedesignClass";

interface MctDialogProps {
  /** Defaults to `true` */
  hasFocusTrap?: boolean;
  mandatory?: boolean;
  /**
   * If set to `true`, actions (CTA buttons), will stack and stretch to 100% at narrower viewport widths
   */
  responsiveActions?: boolean;
}

const DialogFocusTrapContainerComponent: ForwardRefRenderFunction<
  HTMLDivElement,
  {
    children: ReactNode;
  }
> = ({ children }, containerRef) => (
  <div className="mct-c-dialog__focus-trap-container" ref={containerRef}>
    {children}
  </div>
);

const DialogFocusTrapContainer = forwardRef(DialogFocusTrapContainerComponent);

/**
 * Simple wrapper around CMSDS Dialog that only renders `children` when open
 */
export const MctDialog: FCWithChildren<DialogProps & MctDialogProps> = ({
  actionsClassName,
  children,
  className,
  headerClassName,
  isOpen,
  hasFocusTrap = true,
  mandatory,
  responsiveActions,
  ...props
}) => {
  // * Flags
  const mGovRedesignClass = useMgovRedesignClass();

  // * Refs
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const focusTrapOptions = useFocusTrapOptions<HTMLDialogElement>(dialogRef);

  // * Constants
  const uniqueId = useId();
  const renderFocusTrapComponent = !TESTING && hasFocusTrap;
  const WrapperEl = renderFocusTrapComponent ? FocusTrap : Fragment;
  const DialogWrapperEl = renderFocusTrapComponent
    ? DialogFocusTrapContainer
    : Fragment;
  const dialogClassName = "mct-c-dialog";

  // * Effects
  useEffect(() => {
    if (!dialogRef.current) {
      dialogRef.current = document.querySelector(
        `[data-id="${uniqueId}"]`
      ) as HTMLDialogElement | null;
    }
  }, [uniqueId]);

  return (
    <PortalWrapper renderToPortal={renderFocusTrapComponent}>
      <WrapperEl
        {...(renderFocusTrapComponent
          ? { active: isOpen, focusTrapOptions }
          : {})}
      >
        <DialogWrapperEl>
          <Dialog
            actionsClassName={classNames(
              `${dialogClassName}__actions`,
              actionsClassName,
              responsiveActions && `${dialogClassName}__actions--responsive`
            )}
            className={classNames(
              className,
              dialogClassName,
              mGovRedesignClass,
              mandatory && `${dialogClassName}--mandatory`
            )}
            headerClassName={classNames(
              `${dialogClassName}__header`,
              headerClassName
            )}
            isOpen={isOpen}
            data-id={uniqueId}
            {...props}
          >
            {isOpen && children}
          </Dialog>
        </DialogWrapperEl>
      </WrapperEl>
    </PortalWrapper>
  );
};
